import kremkaAsset from "@/assets/cases/kremka-product.webp";
import deinlunuAsset from "@/assets/cases/deinlunu-products.webp";
import gordigearAsset from "@/assets/cases/gordigear-outdoor.webp";
import maskabelImg from "@/assets/cases/maskabel.webp";
import logomattenAsset from "@/assets/cases/logomattenwelt-showroom.webp";
import snydergolfAsset from "@/assets/cases/snydergolf-course.webp";

/**
 * Kennzahlen zentral pflegen.
 * PLATZHALTER: Solange ein Wert "" ist, wird er als "wird ergänzt" ausgegeben 
 * damit nie eine erfundene Zahl online steht. Einfach den String ersetzen.
 */
export type CaseMetric = {
  label: string;
  before: string;
  after: string;
  beforeLabel?: string;
  afterLabel?: string;
};

export type CaseStudy = {
  slug: string;
  shop: string;
  domain: string;
  url: string;
  industry: string;
  claim: string;
  situation: string[];
  work: string[];
  metrics: CaseMetric[];
  image: string;
  imageAlt: string;
  width: number;
  height: number;
  /** Kurze, umsetzungsbezogene Zusammenfassung für Karten. */
  summary: string;
};

const PH = "";

const cr = (before: string, beforeLabel: string, after: string, afterLabel: string): CaseMetric[] => [
  { label: "Kaufabschlussrate", before, beforeLabel, after, afterLabel },
];

export const cases: CaseStudy[] = [
  {
    slug: "kremka",
    shop: "kremka GmbH",
    domain: "kremka-shop.de",
    url: "https://kremka-shop.de/",
    industry: "Chemisch-technische Produkte (B2B)",
    claim: "B2B-Sortiment mit Beratungsbedarf und wiederkehrenden Bestellungen",
    situation: [
      "Gewerbliche Käufer wissen meist genau, was sie brauchen, sie scheitern eher am Finden als am Wollen.",
      "Beratung und Shop standen nebeneinander statt ineinander.",
    ],
    work: [
      "Produktübersicht und Suche als Haupteinstieg statt Imagefläche",
      "Nachbestellung und Mengenlogik für Bestandskunden verkürzt",
      "Beratungsanfrage direkt an den Produkten verankert",
      "Mobile Lesbarkeit technischer Daten und Datenblätter verbessert",
    ],
    metrics: cr("0,90 %", "Januar 2026", "4,20 %", "Juli 2026"),
    image: kremkaAsset,
    imageAlt: "Kremka Brilliant-Finish Autopflegeprodukt vor einem grünen Sportwagen",
    width: 533,
    height: 533,
    summary:
      "Produktübersicht und Suche als Einstieg, kürzere Nachbestellung für Bestandskunden, Beratungsanfrage direkt am Produkt.",
  },
  {
    slug: "deinlunu",
    shop: "Lunu",
    domain: "deinlunu.com",
    url: "https://deinlunu.com/",
    industry: "Food & Süßwaren",
    claim: "Fruchtgummi-Marke mit starkem Sortiment, aber unklarer Kaufreihenfolge",
    situation: [
      "Starke Marke und gute Produktfotos, der Weg vom Startseiten-Besuch zum passenden Produkt war jedoch länger als nötig.",
      "Bundles und Mengenrabatte waren vorhanden, wurden aber erst spät im Funnel sichtbar.",
    ],
    work: [
      "Produktseite neu strukturiert: Nutzenversprechen, Varianten und Bundle-Vorteil oberhalb des ersten Scrolls",
      "Bundle- und Mengenlogik direkt an der Kaufentscheidung platziert statt erst im Warenkorb",
      "Vertrauenselemente (Versand, Bewertungen, Rückgabe) nah an den Kauf-Button gezogen",
      "Mobile Navigation und Kategorie-Einstieg verkürzt",
    ],
    metrics: cr("0,26 %", "April 2026", "3,19 %", "Juli 2026"),
    image: deinlunuAsset,
    imageAlt: "LUNU Schokoladen-Geschenkbox mit fünf verschiedenen Snacksorten",
    width: 1024,
    height: 681,
    summary:
      "Produktseite neu strukturiert, Bundle- und Mengenlogik an die Kaufentscheidung geholt, mobile Kaufwege verkürzt.",
  },
  {
    slug: "gordigear",
    shop: "Gordigear",
    domain: "gordigear.de",
    url: "https://gordigear.de/",
    industry: "Outdoor & Dachzelte (hochpreisig)",
    claim: "Erklärungsbedürftige Produkte mit langem Entscheidungsweg",
    situation: [
      "Hohe Warenkörbe, entsprechend lange Recherchephase, Besucher kamen mehrfach, bevor sie kauften.",
      "Die Unterschiede zwischen den Zeltmodellen waren nicht auf einen Blick erfassbar.",
    ],
    work: [
      "Vergleichslogik zwischen den Modellen sichtbar gemacht (Passung Fahrzeug, Größe, Einsatz)",
      "Kaufhemmnisse bei hohem Warenkorb adressiert: Garantie, Lieferzeit, Zahlungsarten",
      "Produktseite auf Entscheidung statt Aufzählung umgebaut",
      "Mobile Darstellung der Bildergalerien und technischen Daten überarbeitet",
    ],
    metrics: [],
    image: gordigearAsset,
    imageAlt: "GordiGear Dachzelt auf einem Geländewagen an der Küste",
    width: 474,
    height: 370,
    summary:
      "Modellvergleich sichtbar gemacht, Produktseite auf Entscheidung umgebaut, mobile Galerien und Datenblätter überarbeitet.",
  },
  {
    slug: "maskabel",
    shop: "MASKABEL",
    domain: "maskabel.com",
    url: "https://maskabel.com/",
    industry: "Elektro & Kabel (B2C und B2B)",
    claim: "Konfigurationsprodukt: der Kunde muss erst finden, dann rechnen",
    situation: [
      "Kabel werden nach Typ, Adern, Querschnitt und Meterzahl gekauft, jede Unsicherheit im Konfigurator kostet direkt einen Kauf.",
      "Gewerbliche Anfragen und Privatkäufe liefen über denselben Weg.",
    ],
    work: [
      "Kabelfinder als Einstieg auf der Startseite platziert, Auswahl in wenigen Schritten",
      "Preis- und Mengenlogik (Meterpreis, Staffel) transparent an der Auswahl gezeigt",
      "Zweiter Pfad für Großmengen/Angebot getrennt vom normalen Checkout",
      "Mobile Eingabefelder und Tastaturtypen für schnelle Konfiguration optimiert",
    ],
    metrics: [],
    image: maskabelImg,
    imageAlt: "Startseite des Shops maskabel.com mit Kabelfinder",
    width: 1200,
    height: 675,
    summary:
      "Kabelfinder als Einstieg auf der Startseite, transparente Preis- und Mengenlogik, eigener Weg für Großmengen.",
  },
  {
    slug: "logo-matten",
    shop: "Logomatten-Welt",
    domain: "logo-matten.com",
    url: "https://logo-matten.com/",
    industry: "Individualprodukte / Print on Demand (B2B)",
    claim: "Individuell gestaltete Matten, Konfiguration entscheidet über den Umsatz",
    situation: [
      "Der Kunde gestaltet sein Produkt selbst; Abbrüche passieren im Gestaltungs- und Maßschritt, nicht im Checkout.",
      "Preise hängen von Größe und Menge ab, was Unsicherheit erzeugt.",
    ],
    work: [
      "Gestaltungs-Einstieg von der Startseite aus in einen klaren Schrittpfad gebracht",
      "Größen- und Preislogik früher sichtbar gemacht, damit weniger Kunden im Konfigurator abbrechen",
      "Angebotsanfrage als zweiter Abschluss für gewerbliche Mengen etabliert",
      "Vertrauensanker (Lieferzeit, Muster, Rechnungskauf) an den Entscheidungspunkt gesetzt",
    ],
    metrics: [{ label: "Kaufabschlussrate", before: "1,20 %", after: "3,10 %" }],
    image: logomattenAsset,
    imageAlt: "Bedruckte Logomatte in einem Ausstellungsraum der Logomatten-Welt",
    width: 1200,
    height: 800,
    summary:
      "Gestaltungs-Einstieg als klarer Schrittpfad, Größen- und Preislogik früher sichtbar, Angebotsanfrage für gewerbliche Mengen.",
  },
  {
    slug: "snydergolf",
    shop: "Snyder Golf",
    domain: "snydergolf.ch",
    url: "https://snydergolf.ch/",
    industry: "Sport & Golfausrüstung (CH)",
    claim: "Umzug von WooCommerce auf Shopify, inklusive Personalisierung als Umsatztreiber",
    situation: [
      "Der Shop lief auf WooCommerce: Wartung, Plugin-Abhängigkeiten und Ladezeit standen dem Verkauf im Weg.",
      "Der Umsatz konzentriert sich auf wenige Monate, in der Saison zählt jede Kaufabschlussrate doppelt.",
      "Personalisierte Bälle sind margenstark, waren aber ein Nebenpfad statt ein Hauptweg.",
    ],
    work: [
      "Komplette Migration von WooCommerce zu Shopify: Produkte, Varianten, Kunden und Bestellungen übernommen",
      "URL-Struktur mit Weiterleitungen erhalten, damit Rankings und Bestandslinks nicht verloren gehen",
      "Personalisierung als eigenständigen, sichtbaren Kaufweg aufgebaut",
      "Kollektions- und Produktseiten auf schnelle Auswahl (Ballmodell, Menge) reduziert",
      "Versand- und Zahlungshinweise für die Schweiz früh im Funnel gezeigt",
    ],
    metrics: cr("0,79 %", "März 2026", "3,59 %", "Juli 2026"),
    image: snydergolfAsset,
    imageAlt: "Snyder-Golfspieler beim Platzieren eines Golfballs auf dem Grün",
    width: 1200,
    height: 675,
    summary:
      "Migration von WooCommerce zu Shopify inklusive Weiterleitungen, Personalisierung als eigener Kaufweg, schnellere Kollektionsseiten.",
  },
];

export const getCase = (slug: string) => cases.find((c) => c.slug === slug);

export const isPlaceholder = (v: string) => v.trim() === PH || v.trim() === "";
